import React, { useEffect } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { Check, MapPin, Monitor, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { LOCAL_PAGES, LOCAL_AREA } from "@/data/localPages";

export default function LocalLessons() {
  const slug = useLocation().pathname.replace(/^\/+|\/+$/g, "");
  const pg = LOCAL_PAGES[slug];
  useEffect(() => {
    if (!pg) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify({ "@context": "https://schema.org", "@type": "Service", name: pg.subject, serviceType: pg.subject, description: pg.description,
      provider: { "@id": "https://thejthub.com/#org" }, areaServed: [{ "@type": "City", name: "Dartford" }, { "@type": "Country", name: "United Kingdom" }] });
    document.head.appendChild(el);
    return () => el.remove();
  }, [pg]);
  if (!pg) return <Navigate to="/" replace />;
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-8 lg:py-14" data-testid={`local-page-${slug}`}>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">{pg.subject} · {LOCAL_AREA}</p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl" data-testid="local-title">{pg.h1}</h1>
        <p className="mt-4 text-lg text-muted-foreground">{pg.description}</p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="local-in-person">
            <MapPin size={20} className="text-primary" />
            <h2 className="mt-2 font-serif text-xl">In person in {LOCAL_AREA}</h2>
            <p className="mt-1 text-sm text-muted-foreground">1:1 lessons with Jason in the Dartford area. <Link to="/pricing" className="font-semibold text-primary hover:underline" data-testid="local-book">Book a 1:1 lesson</Link> and mention you'd like to meet in person.</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="local-online">
            <Monitor size={20} className="text-primary" />
            <h2 className="mt-2 font-serif text-xl">Online, anywhere in the UK</h2>
            <p className="mt-1 text-sm text-muted-foreground">Step-by-step courses you can take at home, plus 1:1 video sessions with Jason.</p>
          </div>
        </div>
        <ul className="mt-8 space-y-2">{pg.points.map((p) => <li key={p} className="flex items-start gap-2"><Check size={18} className="mt-0.5 shrink-0 text-primary" />{p}</li>)}</ul>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to={pg.course} data-testid="local-courses" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground transition-transform hover:scale-105">See the courses <ArrowRight size={15} /></Link>
          <Link to="/pricing" data-testid="local-pricing" className="inline-flex items-center gap-2 rounded-full border border-primary px-6 py-3 text-sm font-bold text-primary hover:bg-primary/10">1:1 lessons &amp; prices</Link>
          <Link to="/about" data-testid="local-about" className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold hover:border-primary">About Jason</Link>
        </div>
        <p className="mt-10 text-sm text-muted-foreground">Also: {Object.entries(LOCAL_PAGES).filter(([k]) => k !== slug).map(([k, v], i) => <React.Fragment key={k}>{i ? " · " : ""}<Link to={`/${k}`} className="font-semibold text-primary hover:underline" data-testid={`local-link-${k}`}>{v.subject} in Dartford</Link></React.Fragment>)}</p>
      </main>
    </div>
  );
}
